// @flow

import appendQuery from 'append-query';
import { parse } from 'query-string';
import {
  FLUSH_CACHED_ROUTES,
  GET_ROUTE,
  GET_ROUTE_SUCCESS,
  GET_ROUTE_FAILED,
  ACTIVATE_ITEM,
} from './constants';
import flattenError from '../../helpers/flattenError';
import { isMultiLanguage, fetchData } from '../../helpers';
import { getSlug, getSearch } from '../router';
import {
  needsLoading,
  getPdbIdFromSlug,
} from './selectors';
import {
  receivePdbData,
  setActivePdbItem,
} from '../pdb';
import { receiveActivesData } from '../configuration/actives';
import { receiveConfigBuilder } from '../configuration';
import { receivedNews } from '../news';
import { receivedBlog } from '../blog';
import { receivedReview } from '../review';
import {
  getActiveLanguage,
  setActiveLanguage,
} from '../activeLanguage';
import type {
  FetchingRoute,
  FetchRouteSuccess,
  FetchRouteFailed,
  FlushCachedRoutes,
  ActivateItem,
  Response,
  Slug,
} from './types';
import type {
  APIRoute,
} from '../../types';
import type { FetchError, Arguments } from '../../helpers/fetchData';

export const flushCachedRoutes = (): FlushCachedRoutes => ({
  type: FLUSH_CACHED_ROUTES,
});

export const fetchingRoute = (slug: Slug): FetchingRoute => ({
  type: GET_ROUTE,
  slug,
});

export const fetchRouteSuccess = (slug: Slug, response: Response): FetchRouteSuccess => ({
  type: GET_ROUTE_SUCCESS,
  slug,
  response,
});

export const fetchRouteFailed = (slug: Slug, err: Error | FetchError): FetchRouteFailed => ({
  type: GET_ROUTE_FAILED,
  slug,
  response: flattenError(err),
});

export const activateItem = (slug: Slug): ActivateItem => ({
  type: ACTIVATE_ITEM,
  slug,
});

export const updateActivePdbId = (slug: Slug) => (dispatch: Function, getState: Function) => {
  const state = getState();
  const pdbId = getPdbIdFromSlug(state, slug);

  if (pdbId) {
    dispatch(setActivePdbItem(pdbId));
  }
};

type Fetcher = (_: Arguments) => Promise<{ json: APIRoute }>;

// @TODO: remove forced from this function: https://justlease.atlassian.net/browse/LISTO-345
// SSR will run this function with fetchRoute => fetchDataWithAuth
// frontend will run this will fallback fetchData
export default (
  fn: Fetcher = fetchData,
  forced?: boolean = false,
) => async (dispatch: Function, getState: Function) => {
  const state = getState();
  const slug = getSlug(state);

  if (!forced && !needsLoading(state, slug)) {
    // set the active pdb id and switch the route
    dispatch(updateActivePdbId(slug));
    dispatch(activateItem(slug));
    return;
  }

  const search = getSearch(state);
  const searchParams = (search && parse(search)) || {};

  const queryParams = {
    ...searchParams,
    slug,
  };

  const language = isMultiLanguage() ? getActiveLanguage(state) : null;
  const endpoint = appendQuery('', queryParams);

  dispatch(fetchingRoute(slug));

  let response;
  try {
    const { json } = await fn({ language, endpoint });
    response = json;
  } catch (err) {
    dispatch(fetchRouteFailed(slug, err));
    throw err;
  }

  if (response.pdb) {
    dispatch(receivePdbData(response.pdb));
  }

  if (response.acf && response.acf.configbuilder) {
    dispatch(receiveConfigBuilder(response.acf.configbuilder));
  }

  if (response.news) {
    dispatch(receivedNews(response.news));
  }

  if (response.blog) {
    dispatch(receivedBlog(response.blog));
  }

  if (response.reviews) {
    dispatch(receivedReview(response.reviews));
  }

  if (response.language) {
    dispatch(setActiveLanguage(response.language));
  }

  dispatch(fetchRouteSuccess(slug, response));

  dispatch(updateActivePdbId(slug));
  if (response.actives) {
    // this fn needs an activePdbId!
    dispatch(receiveActivesData(response.actives));
  }

  dispatch(activateItem(slug));
};
